import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { CATEGORIES, DOC_STAGES, fieldLabel, isKnownField, signingOf, type DocCategory } from '@pti/domain';
import { api } from '../api';
import { useCan } from '../hooks';
import { fmtDateTime } from '../format';
import { ErrorNote, Icon, Loading, Pill, Section, toast } from '../components/ui';
import { Change, ConfirmFlow } from '../components/Confirm';

export interface TemplateInfo {
  key: string; name: string; kind: 'text' | 'word'; category: string; categoryLabel: string; stage: string | null; stageLabel: string; description: string | null;
  signers: string[]; fields: string[]; fileName: string | null; version: number; archived: boolean; updatedAt: string; updatedBy: string | null; body?: string;
}
export interface TemplateMetaInput { name: string; category: DocCategory; stage: string | null; description: string | null; signers: string[] }

const SIGNER_LABEL: Record<string, string> = { person: 'The person', director: 'Director', rtom: 'RTO Manager' };
export const signerText = (s: string[]) => (s.length ? s.map((x) => SIGNER_LABEL[x] ?? x).join(' → ') : 'Nobody — filed without signing');

/** Name, where it fits in the lifecycle, document type and signing order. */
export function TemplateDetailsFields({ value, onChange }: { value: TemplateMetaInput; onChange: (v: TemplateMetaInput) => void }) {
  const up = (p: Partial<TemplateMetaInput>) => onChange({ ...value, ...p });
  const toggle = (s: string) => up({ signers: value.signers.includes(s) ? value.signers.filter((x) => x !== s) : [...value.signers, s] });
  return (
    <>
      <label className="field" htmlFor="t-name"><span>Template name</span><input id="t-name" className="txt" required value={value.name} onChange={(e) => up({ name: e.target.value })} /></label>
      <div className="two">
        <label className="field" htmlFor="t-stage"><span>Where it fits</span>
          <select id="t-stage" value={value.stage ?? ''} onChange={(e) => up({ stage: e.target.value || null })}>
            {DOC_STAGES.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}<option value="">Other</option>
          </select>
        </label>
        <label className="field" htmlFor="t-cat"><span>Type of document</span>
          <select id="t-cat" value={value.category} onChange={(e) => up({ category: e.target.value as DocCategory })}>
            {Object.entries(CATEGORIES).map(([k, v]) => <option key={k} value={k}>{v.label}{v.restricted ? ' (Director / RTOM only)' : ''}</option>)}
          </select>
        </label>
      </div>
      {value.category === 'offer' && <p className="small dim">“Letter of offer” templates appear in <strong>Send letter of offer</strong>. Signing one accepts the offer.</p>}
      <div className="field"><span>Who signs, in order</span>
        <div className="chips">{(['person', 'director', 'rtom'] as const).map((k) => (
          <button type="button" key={k} className={`chip${value.signers.includes(k) ? ' on' : ''}`} onClick={() => toggle(k)}>{value.signers.includes(k) ? `${value.signers.indexOf(k) + 1}. ` : ''}{SIGNER_LABEL[k]}</button>
        ))}</div>
        <span className="small dim">Click in the order they sign. None selected = filed without signing.</span>
      </div>
      <label className="field" htmlFor="t-desc"><span>Notes <span className="dim">(optional — when to use it)</span></span><input id="t-desc" className="txt" value={value.description ?? ''} onChange={(e) => up({ description: e.target.value })} /></label>
    </>
  );
}

export function Templates() {
  const can = useCan();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const [showArchived, setShowArchived] = useState(false);
  const [editing, setEditing] = useState<TemplateInfo | null>(null);
  const [archiving, setArchiving] = useState<TemplateInfo | null>(null);
  const manage = can('templates:manage');
  const q = useQuery({ queryKey: ['templates', 'all'], queryFn: () => api.get<TemplateInfo[]>(`/api/templates${manage ? '?archived=1' : ''}`) });
  const status = useQuery({ queryKey: ['templates', 'status'], queryFn: () => api.get<{ exactLayout: boolean }>('/api/templates/status') });
  const preview = useMutation({
    mutationFn: (key: string) => api.blob(`/api/templates/${key}/preview`, { personId: null, fields: {} }),
    onSuccess: (b) => api.openBlob(b), onError: (e: Error) => toast(e.message),
  });
  const download = useMutation({
    mutationFn: async (t: TemplateInfo) => {
      const b = await api.blob(`/api/templates/${t.key}/file`);
      const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = t.fileName ?? `${t.name}.docx`; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 30_000);
    },
    onError: (e: Error) => toast(e.message),
  });
  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorNote error={q.error} />;
  const list = q.data!.filter((t) => showArchived || !t.archived);
  const groups = [...DOC_STAGES, { key: '', label: 'Other' }].map((s) => ({ ...s, items: list.filter((t) => (t.stage ?? '') === s.key) })).filter((g) => g.items.length);
  const justSaved = params.get('saved');

  return (
    <div className="page">
      <Link className="link back" to="/documents"><Icon n="back" s={15} /> Documents</Link>
      <div className="page-h">
        <div><p className="eyebrow">Documents</p><h1>Templates</h1><p className="lede">Your own Word contracts and letters, prepared once with fields, then filled in for each person and sent for signing.</p></div>
        {manage && <Link className="btn primary" to="/templates/new">+ Upload a Word document</Link>}
      </div>

      {status.data && !status.data.exactLayout && q.data!.some((t) => t.kind === 'word') && (
        <div className="callout warn small"><strong>Word layout not set up yet.</strong> Documents made from Word templates are currently turned into a plain PDF on PTI letterhead. Finish SETUP step 9 (SharePoint conversion) so the PDF looks exactly like your Word document.</div>
      )}

      {groups.map((g) => (
        <Section key={g.key || 'other'} title={g.label}>
          <div className="tpl-rows">
            {g.items.map((t) => (
              <div key={t.key} className={`tpl-row${t.archived ? ' archived' : ''}${justSaved === t.key ? ' fresh' : ''}`}>
                <div className="tpl-main">
                  <span className="row gap-s wrap"><Icon n="doc" s={16} /><strong>{t.name}</strong>
                    {t.kind === 'word' ? <Pill tone="accent">Word</Pill> : <Pill>Built-in text</Pill>}
                    {t.archived && <Pill>Archived</Pill>}
                  </span>
                  <span className="small dim">{t.categoryLabel} · Signs: {signerText(t.signers)}{t.kind === 'word' ? ` · Version ${t.version} · ${fmtDateTime(t.updatedAt)}` : ''}</span>
                  {t.description && <span className="small">{t.description}</span>}
                  <span className="small">{t.fields.length ? t.fields.map((k) => <span key={k} className={`fld-tag${signingOf(k) ? ' sig' : isKnownField(k) && k !== 'offer.conditions' ? '' : ' typed'}`}>{fieldLabel(k)}</span>) : <span className="dim">No fields</span>}</span>
                </div>
                <div className="row gap-s wrap tpl-actions">
                  <button className="btn sm" disabled={preview.isPending} onClick={() => preview.mutate(t.key)}>Preview</button>
                  {manage && t.kind === 'word' && !t.archived && <Link className="btn sm" to={`/templates/${t.key}/prepare`}>Prepare again</Link>}
                  {manage && t.kind === 'word' && <button className="btn sm" onClick={() => download.mutate(t)}>Download Word</button>}
                  {manage && !t.archived && <button className="btn sm" onClick={() => setEditing(t)}>Details</button>}
                  {manage && <button className={`btn sm${t.archived ? '' : ' danger'}`} onClick={() => setArchiving(t)}>{t.archived ? 'Restore' : 'Archive'}</button>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      ))}
      {manage && <label className="check small" htmlFor="show-arch"><input id="show-arch" type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} /> Show archived templates</label>}

      {manage && <OrgDetails />}

      {editing && <DetailsDialog t={editing} onClose={() => setEditing(null)} onDone={() => { void qc.invalidateQueries({ queryKey: ['templates'] }); toast('Saved'); }} />}
      {archiving && (
        <ConfirmFlow title={`${archiving.archived ? 'Restore' : 'Archive'} ${archiving.name}`} onClose={() => setArchiving(null)}
          review={() => <p>{archiving.archived ? `“${archiving.name}” will be available again for making documents.` : `“${archiving.name}” will no longer be offered when making documents. Documents already made from it are not affected, and you can restore it later.`}</p>}
          confirmLabel={archiving.archived ? 'Yes, restore it' : 'Yes, archive it'} danger={!archiving.archived}
          onConfirm={() => api.send('POST', `/api/templates/${archiving.key}/archive`, { archived: !archiving.archived })}
          onDone={() => { void qc.invalidateQueries({ queryKey: ['templates'] }); toast(archiving.archived ? 'Restored' : 'Archived'); }}>
          <p>{archiving.archived ? 'Restore this template?' : 'Archive this template?'}</p>
        </ConfirmFlow>
      )}
    </div>
  );
}

function DetailsDialog({ t, onClose, onDone }: { t: TemplateInfo; onClose: () => void; onDone: () => void }) {
  const orig: TemplateMetaInput = { name: t.name, category: t.category as DocCategory, stage: t.stage, description: t.description ?? '', signers: t.signers };
  const [meta, setMeta] = useState<TemplateMetaInput>(orig);
  const stageLabel = (s: string | null) => DOC_STAGES.find((x) => x.key === s)?.label ?? 'Other';
  return (
    <ConfirmFlow title={`Details of ${t.name}`} onClose={onClose} canContinue={meta.name.trim().length >= 2}
      review={() => (
        <>
          <p>Change the template’s details. Documents already made are not changed.</p>
          <ul className="plain-list">
            <Change label="Name" from={orig.name} to={meta.name} />
            <Change label="Where it fits" from={stageLabel(orig.stage)} to={stageLabel(meta.stage)} />
            <Change label="Type" from={CATEGORIES[orig.category].label} to={CATEGORIES[meta.category].label} />
            <Change label="Signed by" from={signerText(orig.signers)} to={signerText(meta.signers)} />
          </ul>
        </>
      )}
      confirmLabel="Yes, save the details"
      onConfirm={() => { const fd = new FormData(); fd.append('meta', JSON.stringify({ ...meta, description: meta.description?.trim() || null })); return api.upload(`/api/templates/${t.key}`, fd); }}
      onDone={onDone}>
      <TemplateDetailsFields value={meta} onChange={setMeta} />
    </ConfirmFlow>
  );
}

interface Org { name: string; abn: string; rto: string; address: string; phone: string; website: string; director: string }
const ORG_LABEL: Record<keyof Org, string> = { name: 'Company name', abn: 'ABN', rto: 'RTO number', address: 'Address', phone: 'Phone', website: 'Website', director: 'Director name' };

function OrgDetails() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['org'], queryFn: () => api.get<Org>('/api/settings/org') });
  const [f, setF] = useState<Org | null>(null);
  const [confirm, setConfirm] = useState(false);
  if (!q.data) return null;
  const v = f ?? q.data;
  const changed = (Object.keys(ORG_LABEL) as (keyof Org)[]).filter((k) => v[k] !== q.data![k]);
  return (
    <Section title="PTI details used in templates" aside={<span className="small dim">Filled into the “PTI” fields</span>}>
      <div className="org-grid">
        {(Object.keys(ORG_LABEL) as (keyof Org)[]).map((k) => (
          <label key={k} className="field" htmlFor={`org-${k}`}><span>{ORG_LABEL[k]}</span><input id={`org-${k}`} className="txt" value={v[k]} onChange={(e) => setF({ ...v, [k]: e.target.value })} /></label>
        ))}
      </div>
      <div className="row gap"><button className="btn primary" disabled={!changed.length} onClick={() => setConfirm(true)}>Save PTI details</button>{changed.length > 0 && <button className="btn" onClick={() => setF(null)}>Discard changes</button>}</div>
      {confirm && (
        <ConfirmFlow title="Save PTI details" onClose={() => setConfirm(false)}
          review={() => <><p>Documents made from now on will use:</p><ul className="plain-list">{changed.map((k) => <Change key={k} label={ORG_LABEL[k]} from={q.data![k]} to={v[k]} />)}</ul></>}
          confirmLabel="Yes, save" onConfirm={() => api.send('PUT', '/api/settings/org', v)}
          onDone={() => { setF(null); void qc.invalidateQueries({ queryKey: ['org'] }); toast('Saved'); }}>
          <p>Save the changed PTI details?</p>
        </ConfirmFlow>
      )}
    </Section>
  );
}
